import { useMemo } from 'react'
import { ROUTE_IDS } from '~/components/quick-checkout/QuickCheckout.config'
import { AssetType } from '~/types/challenge'
import type { TradingPlatform } from '~/types/challenge'
import type {
  CfdOffersResponse,
  EvaluationType,
  FuturesCategory,
  PayoutType,
  ProductTypeName,
  ScalingPlan,
  TechnologyPlatform,
} from '~/types/offer'

export const PROGRAM_PAYOUT_TYPE_MAP = {
  prime: 2,
  classic: 1,
  instant: 3,
} as const
export const PAYOUT_TYPE_PROGRAM_MAP = {
  2: 'prime',
  1: 'classic',
  3: 'instant',
} as const

export const EVALUATION_TYPE_PHASE_MAP = {
  '1-phase': '1',
  '2-phase': '2',
} as const

export type DefaultSelections = {
  program?: number
  type?: number
  balance?: number
  platform?: number
}
export function getDefaultChallengeSelections(url: string): DefaultSelections {
  const params = new URL(url).searchParams

  const program = params.get('program')?.toLocaleLowerCase()
    ? PROGRAM_PAYOUT_TYPE_MAP[
        (params.get('program')?.toLocaleLowerCase() as keyof typeof PROGRAM_PAYOUT_TYPE_MAP) ?? ''
      ]
    : undefined

  const type = (
    {
      '1': 1,
      '2': 2,
    } as const
  )[params.get('type')?.toLocaleLowerCase() ?? '']

  const balance = Number.isFinite(Number(params.get('balance') ?? undefined))
    ? Number(params.get('balance') ?? undefined)
    : undefined

  const platformId = Number.isFinite(Number(params.get('platform') ?? undefined))
    ? Number(params.get('platform') ?? undefined)
    : undefined

  return { program, type, balance, platform: platformId }
}

export function getAvailableCFDAccountBalances(
  data: any,
  selectedPayoutType: number | null,
  selectedEvaluationType: EvaluationType | null,
): number[] {
  // Map over the offers array to extract the accountBalance values
  const balances: number[] = []

  data.forEach((entry: any) => {
    if (entry.payoutType === selectedPayoutType) {
      entry.evaluationTypes.forEach((evaluation: EvaluationType) => {
        if (evaluation.evaluationType === selectedEvaluationType?.evaluationType) {
          evaluation.offers.forEach((offer) => {
            balances.push(offer.accountBalance)
          })
        }
      })
    }
  })

  // Remove duplicates by creating a Set and converting it back to an array
  return [...new Set(balances)].sort((a, b) => a - b)
}

export function findMatchingCFDOffer(
  data: any,
  selectedPayoutType: number | null,
  selectedEvaluationType: EvaluationType | null,
  selectedAccountBalance: number | null,
) {
  const matchingOffer = data
    .find((entry) => entry.payoutType === selectedPayoutType)
    ?.evaluationTypes.find((evaluation) => evaluation.evaluationType === selectedEvaluationType?.evaluationType)
    ?.offers.find((offer) => offer.accountBalance === selectedAccountBalance)

  if (!matchingOffer) return null

  return {
    ...matchingOffer,
    payoutType: selectedPayoutType,
    evaluationType: selectedEvaluationType?.evaluationType,
  }
}

export function getAvailableCFDPlatforms(
  data: any,
  selectedPayoutType: number | null,
  selectedEvaluationType: EvaluationType | null,
  selectedAccountBalance: number | null,
): TechnologyPlatform[] {
  // Find the matching offer for the given parameters
  const matchingOffer = findMatchingCFDOffer(data, selectedPayoutType, selectedEvaluationType, selectedAccountBalance)

  // If no matching offer is found, return an empty array
  if (!matchingOffer) return []

  return (matchingOffer.technologyPlatforms || []).sort((a: TechnologyPlatform, b: TechnologyPlatform) => a.id - b.id)
}

export function getSelectedCFDOfferDetails(
  data: any,
  selectedPayoutType: number | null,
  selectedEvaluationType: EvaluationType | null,
  selectedAccountBalance: number | null,
) {
  // Find the matching offer for the given parameters
  const matchingOffer = findMatchingCFDOffer(data, selectedPayoutType, selectedEvaluationType, selectedAccountBalance)

  if (!matchingOffer) return null

  return matchingOffer
}

export function getRecommendedCFDAccountBalance(
  data: any,
  selectedPayoutType: number | null,
  selectedEvaluationType: EvaluationType | null,
) {
  for (const entry of data) {
    if (entry.payoutType === selectedPayoutType) {
      for (const evaluation of entry.evaluationTypes) {
        if (evaluation.evaluationType === selectedEvaluationType?.evaluationType) {
          for (const offer of evaluation.offers) {
            if (offer.recommended) {
              return offer.accountBalance
            }
          }
        }
      }
    }
  }

  return getAvailableCFDAccountBalances(data, selectedPayoutType, selectedEvaluationType)[0]
}

export function getAvailableCFDEvaluationTypes(data: any, payoutType: number | null): EvaluationType[] | undefined {
  if (payoutType !== null) {
    return data.find((item: PayoutType) => item.payoutType === payoutType)?.evaluationTypes
  }

  return []
}

export function getRecommendedCFDPlatform(
  data: any,
  payoutType: number | null,
  evaluationType: EvaluationType | null,
  accountBalance: number | null,
) {
  // Get the available scaling plans using the helper function
  const availableTechnologyPlatforms = getAvailableCFDPlatforms(data, payoutType, evaluationType, accountBalance)

  // Find the scaling plan with the recommended technology platform
  return availableTechnologyPlatforms.find((platform) => platform.recommended) || null
}

export function useRecommendedCfdOptions(cfdDecisionTreeData: PayoutType[]) {
  // Get the recommended payout type
  const recommendedPayoutType = useMemo(
    () => cfdDecisionTreeData.find((item) => item.recommended) || null,
    [cfdDecisionTreeData],
  )

  // Get evaluation types for the recommended payout type
  const evaluationTypesForRecommendedPayout = useMemo(
    () =>
      recommendedPayoutType
        ? getAvailableCFDEvaluationTypes(cfdDecisionTreeData, recommendedPayoutType.payoutType)
        : [],
    [cfdDecisionTreeData, recommendedPayoutType],
  )

  // Get the recommended evaluation type
  const recommendedEvaluationType = useMemo(
    () => evaluationTypesForRecommendedPayout?.find((item) => item.recommended) || null,
    [evaluationTypesForRecommendedPayout],
  )

  // Get the recommended account balance
  const recommendedAccountBalance = useMemo(
    () =>
      recommendedEvaluationType && recommendedPayoutType
        ? getRecommendedCFDAccountBalance(
            cfdDecisionTreeData,
            recommendedPayoutType.payoutType,
            recommendedEvaluationType,
          )
        : null,
    [cfdDecisionTreeData, recommendedPayoutType, recommendedEvaluationType],
  )

  // Get the recommended scaling plan object
  const recommendedPlatform = useMemo(
    () =>
      recommendedEvaluationType && recommendedPayoutType && recommendedAccountBalance
        ? getRecommendedCFDPlatform(
            cfdDecisionTreeData,
            recommendedPayoutType.payoutType,
            recommendedEvaluationType,
            recommendedAccountBalance,
          )
        : null,
    [cfdDecisionTreeData, recommendedPayoutType, recommendedEvaluationType, recommendedAccountBalance],
  )

  return {
    recommendedPayoutType,
    recommendedEvaluationType,
    recommendedAccountBalance,
    recommendedPlatform,
  }
}

// FUTURES .....
export function getAvailableFuturesAccountBalances(data: any, selectedPayoutType: number | null): FuturesCategory[] {
  // Array to hold all offer objects
  const offers: any[] = []
  const seenAccBalances = new Set<number>() // To track unique account balances

  data.forEach((entry: any) => {
    if (entry.payoutType === selectedPayoutType) {
      entry.evaluationTypes.forEach((evaluation: EvaluationType) => {
        evaluation.offers.forEach((offer: any) => {
          if (!seenAccBalances.has(offer.accountBalance)) {
            // Add the offer only if the account balance hasn't been collected yet
            offers.push({ ...offer, payoutType: selectedPayoutType, evaluationType: evaluation.evaluationType })
            seenAccBalances.add(offer.accountBalance) // Track the balance
          }
        })
      })
    }
  })

  return offers
}

// *** Get the available Futures Offers based on given payout type ***
export function getAvailableFuturesOffers(data: any, selectedPayoutType: number | null): any[] {
  const offers: any[] = []

  data.forEach((entry: any) => {
    if (entry.payoutType === selectedPayoutType) {
      entry.evaluationTypes.forEach((evaluation: any) => {
        evaluation.offers.forEach((offer: any) => {
          // Add the evaluationType to each offer object
          offers.push({
            ...offer,
            payoutType: selectedPayoutType,
            evaluationType: evaluation.evaluationType,
          })
        })
      })
    }
  })

  return offers
}

export function findSelectedFuturesOfferAndScalingPlan(
  offers: any[],
  selectedAccountBalance: number,
  selectedPlatformId: number | undefined,
): { selectedFuturesOffer: any; selectedScalingPlan: ScalingPlan | null } {
  // const { selectedFuturesOffer, selectedScalingPlan } = useMemo(() => {
  //   let offer = null
  //   let plan = null

  //   offers?.some((currentOffer: any) => {
  //     if (
  //       currentOffer.accountBalance === selectedAccountBalance &&
  //       currentOffer.scalingPlans.some((scalingPlan: any) =>
  //         scalingPlan?.technology?.marketDataProvider?.marketDataPlatforms.some(
  //           (tp: TradingPlatform) => tp.id === selectedPlatformId,
  //         ),
  //       )
  //     ) {
  //       offer = currentOffer

  //       // Find the first matching scaling plan
  //       plan = currentOffer.scalingPlans.find((scalingPlan: any) =>
  //         scalingPlan?.technology?.marketDataProvider?.marketDataPlatforms.some(
  //           (tp: TradingPlatform) => tp.id === selectedPlatformId,
  //         ),
  //       )

  //       // Exit loop early
  //       return true
  //     }
  //     return false
  //   })

  //   return { selectedFuturesOffer: offer, selectedScalingPlan: plan }
  // }, [offers, selectedPlatformId, selectedAccountBalance])

  const { selectedFuturesOffer, selectedScalingPlan } = (() => {
    let offer = null
    let plan = null

    offers?.some((currentOffer: any) => {
      if (
        currentOffer.accountBalance === selectedAccountBalance &&
        currentOffer.scalingPlans.some((scalingPlan: any) =>
          scalingPlan?.technology?.marketDataProvider?.marketDataPlatforms.some(
            (tp: TradingPlatform) => tp.id === selectedPlatformId,
          ),
        )
      ) {
        offer = currentOffer

        // Find the first matching scaling plan
        plan = currentOffer.scalingPlans.find((scalingPlan: any) =>
          scalingPlan?.technology?.marketDataProvider?.marketDataPlatforms.some(
            (tp: TradingPlatform) => tp.id === selectedPlatformId,
          ),
        )

        // Exit loop early
        return true
      }
      return false
    })

    return { selectedFuturesOffer: offer, selectedScalingPlan: plan }
  })()

  return {
    selectedFuturesOffer,
    selectedScalingPlan,
  }
}

export function extractCfdPayoutTypes(
  response: CfdOffersResponse | null | undefined,
  productType: ProductTypeName,
  assetGroupName: string = 'CFD',
): PayoutType[] {
  const product = response?.productTypes?.find((p) => p.productType === productType)
  const group = product?.assetGroups?.find((g) => g.assetGroup.name === assetGroupName)
  return group?.payoutTypes ?? []
}

// Filter out the offers with "offerDisplayType" - "HIDDEN"
export function filterHiddenOffers(offersData: Array<PayoutType>): Array<PayoutType> {
  return offersData.map((payoutType) => ({
    ...payoutType,
    evaluationTypes: payoutType.evaluationTypes.map((evaluationType) => ({
      ...evaluationType,
      offers: evaluationType.offers.filter(({ offerDisplayType }) => offerDisplayType !== 'HIDDEN'),
    })),
  }))
}

// This is used only for QC, to determine Log in now redirect URL.
export function getRedirectUrl(pathname: string, searchParams: URLSearchParams) {
  const allowedParams = ['program', 'type', 'balance', 'platform']

  const filteredParams = new URLSearchParams()
  allowedParams.forEach((param) => {
    if (searchParams.has(param)) {
      filteredParams.set(param, searchParams.get(param)!)
    }
  })

  const redirectMap: Record<string, string> = {
    [ROUTE_IDS.challengeCfd]: '/challenges/cfd',
    [ROUTE_IDS.challengeFutures]: '/challenges/futures',
    [ROUTE_IDS.challengeStocks]: '/challenges/stocks',
  }

  const matchedPath = Object.keys(redirectMap).find((key) => key.endsWith(pathname))

  return matchedPath
    ? filteredParams.toString()
      ? `${redirectMap[matchedPath]}?${filteredParams.toString()}`
      : redirectMap[matchedPath]
    : '/challenges'
}

export const getGuestCheckoutRedirectUrlOnLogin = (offerId: string, planId: string) => {
  const params = new URLSearchParams()

  params.set('offerId', offerId)
  params.set('planId', planId)

  return `/direct-payment?${params.toString()}`
}
export function getRedirectUrlOnQcPersonalDetails(
  offerDetails: any,
  cfdSelectedPlatform?: number | null,
  isInstant?: boolean,
) {
  if (!offerDetails) return '/'

  let { assetGroup, accountBalance, payoutType, evaluationType } = offerDetails
  if (typeof assetGroup === 'object') {
    assetGroup = assetGroup.displayName
  }

  const program: keyof typeof PROGRAM_PAYOUT_TYPE_MAP = isInstant
    ? 'instant'
    : PAYOUT_TYPE_PROGRAM_MAP[payoutType as keyof typeof PAYOUT_TYPE_PROGRAM_MAP]

  const phase = EVALUATION_TYPE_PHASE_MAP[evaluationType as keyof typeof EVALUATION_TYPE_PHASE_MAP]

  let redirectPath = `/challenges/${assetGroup.toLowerCase()}?program=${program}`

  if (phase) {
    redirectPath += `&type=${phase}`
  }

  redirectPath += `&balance=${accountBalance}`

  if (typeof cfdSelectedPlatform === 'number') {
    redirectPath += `&platform=${cfdSelectedPlatform}`
  }

  return redirectPath
}

type AssetGroup = {
  assetGroup: string
  eligibleForClient: true
}
export const getEligibleTabs = (assetGroups: AssetGroup[]) => {
  return {
    cfd: Boolean(assetGroups?.find((group) => group.assetGroup?.toUpperCase() === AssetType.CFD)?.eligibleForClient),
    futures: Boolean(
      assetGroups?.find((group) => group.assetGroup?.toUpperCase() === AssetType.FUTURES)?.eligibleForClient,
    ),
    stocks: Boolean(
      assetGroups?.find((group) => group.assetGroup?.toUpperCase() === AssetType.STOCKS)?.eligibleForClient,
    ),
    // TODO: Rethink about it *** i.e Iraq issue
    // stocks: Boolean(
    //   assetGroups?.find((group) => group.assetGroup?.toUpperCase() === AssetType.STOCKS)?.eligibleForClient,
    // ),
  }
}
